// 🏥 Facturación Especializada (CxC) — Fase A
// Genera una factura de VENTA a partir del COSTO de facturas recibidas
// (filtradas por proveedor/proyecto). Cada ítem se clasifica en un rubro
// (editable); por rubro se aplican dos factores de rentabilidad:
//   precio = costo × (1 + factor1 + factor2).
// Modo detallado (1 línea por ítem) o agrupado (1 línea por rubro). El
// comprobante se graba en estado "por emitir" (la emisión al SRI es la Fase B).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, ChevronDown, ChevronRight, Download, FilePlus2, FileText, Filter, Loader2, Pencil, Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { DescartarDialog, type DocDescartable } from './DescartarDialog';
import { AnularDialog, type DocAnulable } from './AnularDialog';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { PagosEditor, type PagoCab } from './PagosEditor';
import { NuevaFactura } from './Facturacion';
import { abrirFacturaPdf } from './facturaPdf';
import { labelCentroCosto, useCentrosContables, useCentroCostoModo } from './centroCosto';
import { AvisoRenombre } from '@/components/ui/AvisoRenombre';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const usd = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const selectCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';
const inputCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

interface Rubro { id: string; codigo: string; nombre: string; factor1: number; factor2: number; orden: number; }
interface Partner { id: string; razon_social: string; identificacion: string; tipo_partner?: string[]; }
interface ItemCosto {
  ticket_buy_id: string; factura_serie: string; proveedor: string | null; ruc_emisor: string | null;
  numero_linea: number; descripcion: string; cantidad: number | null; costo: number; rubro: string;
  usado_en?: string | null;
}
interface Generada {
  id: string; fecha_emision: string; cliente: string | null; ruc_receptor?: string | null; importe_total: number;
  estado_emision: string; lineas: number; created_at: string; mensaje_sri?: string | null;
  clave_acceso?: string | null; numero_autorizacion?: string | null; serie_comprobante?: string | null;
  xml_url?: string | null; ride_url?: string | null;
}

// Las rutas de disco /opt/sri/documentos se sirven en /sri-docs (Alias Apache + proxy Vite)
const urlDoc = (ruta: string | null | undefined) =>
  ruta ? ruta.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null;

export function FacturacionEspecializada() {
  const queryClient = useQueryClient();
  const anioActual = new Date().getFullYear();
  const [vista, setVista] = useState<'generar' | 'generadas'>('generar');
  const [editId, setEditId] = useState<string | null>(null);

  // catálogos
  const { data: rubros = [] } = useQuery<Rubro[]>({
    queryKey: ['feRubros'], queryFn: async () => (await apiClient.post('/rpc/fe_rubros', {}, FIN)).data || [],
  });
  const { data: partners = [] } = useQuery<Partner[]>({
    queryKey: ['sriPartners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
    staleTime: 60_000,
  });
  const { data: proveedores = [] } = useQuery<Array<{ ruc: string; razon_social: string }>>({
    queryKey: ['feProveedores'], queryFn: async () => (await apiClient.post('/rpc/fe_proveedores', {}, FIN)).data || [],
    staleTime: 60_000,
  });
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);
  const { modo: modoCentro } = useCentroCostoModo();
  const { data: centrosContables = [] } = useCentrosContables(modoCentro === 'contable');
  const labelCentro = labelCentroCosto(modoCentro);

  // factores editables localmente (precargados del catálogo)
  const [factores, setFactores] = useState<Record<string, { f1: number; f2: number }>>({});
  const factoresDe = (codigo: string) =>
    factores[codigo] || (() => { const r = rubros.find((x) => x.codigo === codigo); return { f1: Number(r?.factor1) || 0, f2: Number(r?.factor2) || 0 }; })();

  // filtros y carga de ítems
  const [filtros, setFiltros] = useState({ ruc: '', proyecto: '', anio: '', mes: '' });
  const [incluirUsados, setIncluirUsados] = useState(false);
  const [items, setItems] = useState<ItemCosto[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [rubroItem, setRubroItem] = useState<Record<string, string>>({}); // override de rubro por ítem
  const [modo, setModo] = useState<'agrupado' | 'detallado'>('agrupado');
  const [clienteId, setClienteId] = useState('');
  const [centroId, setCentroId] = useState('');
  const [categoria, setCategoria] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [pagos, setPagos] = useState<PagoCab[]>([{ forma_pago: '01', total: 0, plazo: 0 }]);
  const { data: categorias = [] } = useQuery<string[]>({
    queryKey: ['sri-categorias-emitidos'], queryFn: async () => (await apiClient.post('/rpc/sri_categorias_emitidos', {}, FIN)).data || [],
  });

  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const keyOf = (i: ItemCosto) => `${i.ticket_buy_id}:${i.numero_linea}`;
  const rubroDe = (i: ItemCosto) => rubroItem[keyOf(i)] ?? i.rubro;

  // Agrupa los ítems por comprobante (factura de costo)
  const grupos = useMemo(() => {
    const m = new Map<string, { ticket_buy_id: string; factura_serie: string; proveedor: string | null; ruc_emisor: string | null; items: ItemCosto[] }>();
    for (const i of items) {
      let g = m.get(i.ticket_buy_id);
      if (!g) { g = { ticket_buy_id: i.ticket_buy_id, factura_serie: i.factura_serie, proveedor: i.proveedor, ruc_emisor: i.ruc_emisor, items: [] }; m.set(i.ticket_buy_id, g); }
      g.items.push(i);
    }
    return [...m.values()];
  }, [items]);

  // Orden asc/desc por columna en DOS niveles: los comprobantes se ordenan por el
  // agregado de la columna y los ítems dentro de cada comprobante por su valor.
  // Por defecto: sin orden (respeta el orden de carga).
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (c: string) => { if (c === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setSortCol(c); setSortDir('asc'); } };
  const gruposOrdenados = useMemo(() => {
    if (!sortCol) return grupos;
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = (a: string | number, b: string | number) =>
      typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
    const valG = (g: (typeof grupos)[number]): string | number =>
      sortCol === 'desc' ? `${g.factura_serie} ${g.proveedor ?? ''}`
        : sortCol === 'cant' ? g.items.length
          : sortCol === 'costo' ? g.items.reduce((x, i) => x + (Number(i.costo) || 0), 0)
            : 0;
    const valI = (i: ItemCosto): string | number =>
      sortCol === 'desc' ? i.descripcion
        : sortCol === 'cant' ? (Number(i.cantidad) || 0)
          : sortCol === 'costo' ? (Number(i.costo) || 0)
            : rubroDe(i);
    return grupos
      .map((g) => ({ ...g, items: [...g.items].sort((a, b) => cmp(valI(a), valI(b)) * dir) }))
      .sort((a, b) => cmp(valG(a), valG(b)) * dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupos, sortCol, sortDir, rubroItem]);
  const SortableTh = ({ col, children, className = '' }: { col: string; children: React.ReactNode; className?: string }) => (
    <th className={`py-2 pr-3 ${className}`}>
      <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(col)}
        title="Ordenar asc/desc (comprobantes por su agregado; ítems dentro de cada comprobante)">
        {children}
        <span className="text-[10px]">{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );

  const cargar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/fe_items_costo', {
      _ruc: filtros.ruc || null,
      _proyecto_id: filtros.proyecto || null,
      _anio: filtros.anio ? Number(filtros.anio) : null,
      _mes: filtros.mes ? Number(filtros.mes) : null,
      _incluir_usados: incluirUsados,
    }, FIN)).data as ItemCosto[],
    onSuccess: (data) => {
      // ACUMULA: agrega los ítems nuevos a los ya cargados (sin duplicar),
      // para componer una factura con varios proveedores y varias facturas.
      setItems((prev) => {
        const existentes = new Set(prev.map(keyOf));
        const nuevos = data.filter((i) => !existentes.has(keyOf(i)));
        if (nuevos.length) setSel((s) => new Set([...s, ...nuevos.map(keyOf)]));
        if (data.length === 0) toast.info('Sin ítems de costo para esos filtros');
        else if (nuevos.length === 0) toast.info('Esos ítems ya estaban agregados');
        else toast.success(`${nuevos.length} ítem(s) agregado(s)`);
        return [...prev, ...nuevos];
      });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudieron cargar los ítems'),
  });

  // resumen por rubro de los ítems seleccionados
  const resumenRubros = useMemo(() => {
    const m = new Map<string, { costo: number; n: number }>();
    for (const i of items) {
      if (!sel.has(keyOf(i))) continue;
      const r = rubroDe(i);
      const cur = m.get(r) || { costo: 0, n: 0 };
      cur.costo += Number(i.costo) || 0; cur.n += 1;
      m.set(r, cur);
    }
    return [...m.entries()].map(([codigo, v]) => {
      const { f1, f2 } = factoresDe(codigo);
      const nombre = rubros.find((x) => x.codigo === codigo)?.nombre || codigo;
      return { codigo, nombre, ...v, f1, f2, precio: Math.round(v.costo * (1 + f1 + f2) * 100) / 100 };
    }).sort((a, b) => b.precio - a.precio);
  }, [items, sel, rubroItem, factores, rubros]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalCosto = resumenRubros.reduce((s, r) => s + r.costo, 0);
  const totalPrecio = resumenRubros.reduce((s, r) => s + r.precio, 0);

  const generar = useMutation({
    mutationFn: async () => {
      const payload = items.filter((i) => sel.has(keyOf(i))).map((i) => {
        const r = rubroDe(i); const { f1, f2 } = factoresDe(r);
        return { ticket_buy_id: i.ticket_buy_id, numero_linea: i.numero_linea,
                 descripcion: i.descripcion, costo: i.costo, rubro: r, f1, f2 };
      });
      return (await apiClient.post('/rpc/fe_generar', {
        _cliente_partner_id: clienteId, _modo: modo, _items: payload, _observaciones: observaciones || null,
        _centro_costo_id: modoCentro === 'contable' ? null : (centroId || null),
        _cc_contable_id: modoCentro === 'contable' ? (centroId || null) : null,
        _categoria: categoria || null,
        _pagos: pagos.filter((p) => Number(p.total) > 0).map((p) => ({ forma_pago: p.forma_pago, total: p.total, plazo: p.plazo, unidad_tiempo: p.plazo > 0 ? 'dias' : null })),
      }, FIN)).data;
    },
    onSuccess: (r: any) => {
      toast.success(r.message);
      setItems([]); setSel(new Set()); setObservaciones(''); setCategoria(''); setPagos([{ forma_pago: '01', total: 0, plazo: 0 }]);
      void queryClient.invalidateQueries({ queryKey: ['feGeneradas'] });
      setVista('generadas');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo generar el comprobante'),
  });

  return (
    <div className="p-6 space-y-6">
      <AvisoRenombre clave="refacturacion-costos" antes="Facturación Especializada" />
      <div className="flex items-center gap-3">
        <FilePlus2 className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Refacturación de Costos</h1>
          <p className="text-sm text-muted-foreground">
            Genera facturas de venta desde el costo de facturas recibidas, con rentabilidad por rubro
          </p>
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {([['generar', 'Generar'], ['generadas', 'Generadas']] as const).map(([k, et]) => (
          <button key={k} onClick={() => { setVista(k); setEditId(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${vista === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {et}
          </button>
        ))}
      </div>

      {vista === 'generar' && (<>
        {/* Filtros de facturas de costo */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="fe-ruc">Proveedor</label>
                <select id="fe-ruc" className={`${selectCls} max-w-72`}
                  value={filtros.ruc} onChange={(e) => setFiltros({ ...filtros, ruc: e.target.value })}>
                  <option value="">Todos</option>
                  {proveedores.map((p) => (
                    <option key={p.ruc} value={p.ruc}>{p.razon_social} ({p.ruc})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="fe-proy">Proyecto</label>
                <select id="fe-proy" className={`${selectCls} max-w-56`} value={filtros.proyecto}
                  onChange={(e) => setFiltros({ ...filtros, proyecto: e.target.value })}>
                  <option value="">Todos</option>
                  {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="fe-anio">Año</label>
                <select id="fe-anio" className={selectCls} value={filtros.anio}
                  onChange={(e) => setFiltros({ ...filtros, anio: e.target.value })}>
                  <option value="">Todos</option>
                  {Array.from({ length: anioActual - 2014 }, (_, i) => anioActual - i).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="fe-mes">Mes</label>
                <select id="fe-mes" className={selectCls} value={filtros.mes}
                  onChange={(e) => setFiltros({ ...filtros, mes: e.target.value })}>
                  <option value="">Todos</option>
                  {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" title="Los ítems ya usados en otra factura se ocultan; márcalo para verlos (salen bloqueados, con la factura que los consumió)">
                <input type="checkbox" checked={incluirUsados} onChange={(e) => setIncluirUsados(e.target.checked)} />
                Incluir ya facturados
              </label>
              <Button size="sm" onClick={() => cargar.mutate()} isLoading={cargar.isPending}>
                {!cargar.isPending && <Filter className="w-4 h-4" />} Agregar ítems
              </Button>
              {items.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => { setItems([]); setSel(new Set()); setRubroItem({}); }}>
                  Limpiar ({items.length})
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Filtra un proveedor y pulsa <b>Agregar ítems</b>; repite con otros proveedores para
              componer una factura con varios. Los ítems se acumulan sin duplicarse.
            </p>
          </CardContent>
        </Card>

        {items.length > 0 && (
          <>
            {/* Resumen por rubro con factores editables */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Rentabilidad por rubro</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="py-2 pr-3">Rubro</th>
                      <th className="py-2 pr-3 text-right">Ítems</th>
                      <th className="py-2 pr-3 text-right">Costo</th>
                      <th className="py-2 pr-3 text-right">Factor 1</th>
                      <th className="py-2 pr-3 text-right">Factor 2</th>
                      <th className="py-2 text-right">Precio venta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumenRubros.map((r) => (
                      <tr key={r.codigo} className="border-b last:border-0">
                        <td className="py-2 pr-3">{r.nombre}</td>
                        <td className="py-2 pr-3 text-right">{r.n}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(r.costo)}</td>
                        <td className="py-2 pr-3 text-right">
                          <input type="number" step="0.01" min="0" className={`${inputCls} w-20 text-right`}
                            value={r.f1}
                            onChange={(e) => setFactores({ ...factores, [r.codigo]: { f1: Number(e.target.value), f2: r.f2 } })} />
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <input type="number" step="0.01" min="0" className={`${inputCls} w-20 text-right`}
                            value={r.f2}
                            onChange={(e) => setFactores({ ...factores, [r.codigo]: { f1: r.f1, f2: Number(e.target.value) } })} />
                        </td>
                        <td className="py-2 text-right whitespace-nowrap font-medium">{usd(r.precio)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold border-t">
                      <td className="py-2 pr-3">Total</td>
                      <td className="py-2 pr-3 text-right">{[...sel].length}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(totalCosto)}</td>
                      <td colSpan={2}></td>
                      <td className="py-2 text-right whitespace-nowrap">{usd(totalPrecio)}</td>
                    </tr>
                  </tfoot>
                </table>
                <p className="text-xs text-muted-foreground mt-2">
                  Precio = costo × (1 + factor1 + factor2). Los factores precargan del catálogo y puedes ajustarlos aquí.
                </p>
              </CardContent>
            </Card>

            {/* Generación */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium">Modo</label>
                    <div className="flex gap-3 h-9 items-center text-sm">
                      <label className="flex items-center gap-1"><input type="radio" checked={modo === 'agrupado'} onChange={() => setModo('agrupado')} /> Agrupado por rubro</label>
                      <label className="flex items-center gap-1"><input type="radio" checked={modo === 'detallado'} onChange={() => setModo('detallado')} /> Detallado ítem por ítem</label>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="fe-cli">Cliente destino *</label>
                    <select id="fe-cli" className={`${selectCls} max-w-72`} value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
                      <option value="">— Seleccione —</option>
                      {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion})</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="fe-cc">{labelCentro} *</label>
                    <select id="fe-cc" className={`${selectCls} max-w-72`} value={centroId} onChange={(e) => setCentroId(e.target.value)}>
                      <option value="">— Seleccione —</option>
                      {modoCentro === 'contable'
                        ? centrosContables.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)
                        : centros.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="fe-cat">Categoría</label>
                    <select id="fe-cat" className={`${selectCls} max-w-72`} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                      <option value="">— (se define al procesar) —</option>
                      {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-48 space-y-1">
                    <label className="text-xs font-medium" htmlFor="fe-obs">Observaciones</label>
                    <input id="fe-obs" className={`${inputCls} w-full`} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Opcional" />
                  </div>
                  <div className="w-full"><PagosEditor pagos={pagos} setPagos={setPagos} total={totalPrecio} /></div>
                  <Button isLoading={generar.isPending} onClick={() => {
                    if (!clienteId) { toast.error('Seleccione el cliente destino'); return; }
                    if (!centroId) { toast.error('Seleccione el centro de costo'); return; }
                    if (sel.size === 0) { toast.error('Seleccione al menos un ítem'); return; }
                    const pv = pagos.filter((p) => Number(p.total) > 0);
                    if (pv.length > 0) {
                      const sp = Math.round(pv.reduce((s, p) => s + Number(p.total), 0) * 100) / 100;
                      if (Math.abs(sp - Math.round(totalPrecio * 100) / 100) > 0.01) { toast.error(`Las formas de pago (${sp.toFixed(2)}) deben sumar el total (${totalPrecio.toFixed(2)})`); return; }
                    }
                    generar.mutate();
                  }}>
                    {!generar.isPending && <Wand2 className="w-4 h-4" />} Generar comprobante
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Ítems de costo (seleccionar / reclasificar) */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Ítems de costo ({items.length}) · {sel.size} seleccionados · {new Set(items.map((i) => i.ruc_emisor)).size} proveedor(es)</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="py-2 pr-2 w-8">
                        <input type="checkbox" checked={sel.size > 0 && sel.size === items.filter((i) => !i.usado_en).length}
                          onChange={() => { const disp = items.filter((i) => !i.usado_en); setSel(sel.size === disp.length ? new Set() : new Set(disp.map(keyOf))); }} />
                      </th>
                      <th className="py-2 pr-3" colSpan={2}>
                        <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort('desc')}
                          title="Ordenar asc/desc (comprobantes y descripciones)">
                          Comprobante · Descripción <span className="text-[10px]">{sortCol === 'desc' ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
                        </button>
                      </th>
                      <SortableTh col="cant">Cant.</SortableTh>
                      <SortableTh col="costo" className="text-right">Costo</SortableTh>
                      <SortableTh col="rubro">Rubro</SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {gruposOrdenados.map((g) => {
                      const keys = g.items.filter((i) => !i.usado_en).map(keyOf);
                      const nSel = keys.filter((k) => sel.has(k)).length;
                      const todos = nSel === keys.length;
                      const abierto = expandidos.has(g.ticket_buy_id);
                      const costoGrupo = g.items.reduce((s, x) => s + (Number(x.costo) || 0), 0);
                      return (
                        <FragmentoGrupo key={g.ticket_buy_id}>
                          {/* Cabecera del comprobante (expande/contrae) */}
                          <tr className="border-b bg-muted/40">
                            <td className="py-2 pr-2">
                              <input type="checkbox" checked={todos}
                                ref={(el) => { if (el) el.indeterminate = nSel > 0 && !todos; }}
                                onChange={() => setSel((p) => {
                                  const s = new Set(p);
                                  if (todos) keys.forEach((k) => s.delete(k));
                                  else keys.forEach((k) => s.add(k));
                                  return s;
                                })} />
                            </td>
                            <td className="py-2 pr-3 whitespace-nowrap font-medium" colSpan={2}>
                              <button type="button" className="inline-flex items-center gap-1"
                                onClick={() => setExpandidos((p) => { const s = new Set(p); s.has(g.ticket_buy_id) ? s.delete(g.ticket_buy_id) : s.add(g.ticket_buy_id); return s; })}>
                                {abierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                {g.factura_serie}
                                <span className="text-xs text-muted-foreground font-normal">· {g.proveedor || g.ruc_emisor}</span>
                              </button>
                            </td>
                            <td className="py-2 pr-3 text-xs text-muted-foreground">{g.items.length} ítem(s) · {nSel} sel.</td>
                            <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(costoGrupo)}</td>
                            <td className="py-2 pr-3"></td>
                          </tr>
                          {/* Ítems del comprobante (si está expandido) */}
                          {abierto && g.items.map((i) => {
                            const k = keyOf(i);
                            return (
                              <tr key={k} className={`border-b last:border-0 hover:bg-muted/40 ${i.usado_en ? 'opacity-60' : ''}`}>
                                <td className="py-2 pr-2 pl-6">
                                  <input type="checkbox" checked={sel.has(k)} disabled={!!i.usado_en}
                                    onChange={() => setSel((p) => { const s = new Set(p); s.has(k) ? s.delete(k) : s.add(k); return s; })} />
                                </td>
                                <td className="py-2 pr-3" colSpan={2}>
                                  <span className="truncate max-w-96 inline-block align-middle pl-2" title={i.descripcion}>{i.descripcion}</span>
                                  {i.usado_en && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" title={`Ya facturado: ${i.usado_en}`}>Facturado · {i.usado_en}</span>}
                                </td>
                                <td className="py-2 pr-3 text-xs text-muted-foreground">{i.cantidad ?? ''}</td>
                                <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(i.costo)}</td>
                                <td className="py-2 pr-3">
                                  <select className={`${selectCls} w-44`} value={rubroDe(i)}
                                    onChange={(e) => setRubroItem({ ...rubroItem, [k]: e.target.value })}>
                                    {rubros.map((r) => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
                                  </select>
                                </td>
                              </tr>
                            );
                          })}
                        </FragmentoGrupo>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </>)}

      {vista === 'generadas' && (editId
        ? (
          <div className="space-y-2">
            <button type="button" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setEditId(null)}>
              <ChevronRight className="w-4 h-4 rotate-180" /> Volver a Generadas (sin guardar)
            </button>
            <NuevaFactura editId={editId}
              onCreada={() => { setEditId(null); void queryClient.invalidateQueries({ queryKey: ['feGeneradas'] }); }}
              onCancelar={() => setEditId(null)} />
          </div>
        )
        : <Generadas onEdit={(id) => setEditId(id)} />)}
    </div>
  );
}

const ESTADO_EMISION: Record<string, { label: string; cls: string }> = {
  por_emitir: { label: 'Por emitir', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  encolado:   { label: 'Encolado',   cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  autorizado: { label: 'Autorizado', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  emitido:    { label: 'Emitido',    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  devuelto:   { label: 'Devuelto',   cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  rechazado:  { label: 'Rechazado',  cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  error:      { label: 'Error',      cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  anulado:    { label: 'Anulado',    cls: 'bg-muted text-muted-foreground line-through' },
  descartado: { label: 'Descartado', cls: 'bg-muted text-muted-foreground' },
};
const EMITIBLE = ['por_emitir', 'rechazado', 'devuelto', 'error'];

function Generadas({ onEdit }: { onEdit: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [descartar, setDescartar] = useState<DocDescartable | null>(null);
  const [anular, setAnular] = useState<DocAnulable | null>(null);
  const { isAdmin } = usePermissions();
  const { data: docs = [], isFetching } = useQuery<Generada[]>({
    queryKey: ['feGeneradas'], queryFn: async () => (await apiClient.post('/rpc/fe_generadas', { _estado: 'todos' }, FIN)).data || [],
    refetchInterval: (q) => (q.state.data || []).some((d: Generada) => d.estado_emision === 'encolado') ? 2500 : false,
  });
  const emitir = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/fe_emitir', { _ticket_id: id }, FIN)).data,
    onSuccess: () => { toast.success('Comprobante encolado para emisión'); void queryClient.invalidateQueries({ queryKey: ['feGeneradas'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo emitir'),
  });

  // Filtros (en cliente: fe_generadas ya trae todo el universo de la empresa)
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [fCliente, setFCliente] = useState('');
  const [fRuc, setFRuc] = useState('');
  const [fNumero, setFNumero] = useState('');
  const [fEstado, setFEstado] = useState('');
  const estados = useMemo(() => [...new Set(docs.map((d) => d.estado_emision))].sort(), [docs]);
  const norm = (v: string | null | undefined) => (v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const filtradas = useMemo(() => docs.filter((d) =>
    (!desde || d.fecha_emision >= desde) && (!hasta || d.fecha_emision <= hasta)
    && (!fCliente || norm(d.cliente).includes(norm(fCliente)))
    && (!fRuc || (d.ruc_receptor || '').includes(fRuc.trim()))
    && (!fNumero || (d.serie_comprobante || '').replace(/-/g, '').includes(fNumero.trim().replace(/-/g, '')))
    && (!fEstado || d.estado_emision === fEstado)
  ), [docs, desde, hasta, fCliente, fRuc, fNumero, fEstado]);
  const hayFiltro = !!(desde || hasta || fCliente || fRuc || fNumero || fEstado);
  const inp = 'h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">
            Comprobantes generados ({hayFiltro ? `${filtradas.length} de ${docs.length}` : docs.length})
          </CardTitle>
          <div className="flex items-end gap-2 flex-wrap text-xs text-muted-foreground">
            <label className="flex flex-col">Desde
              <input type="date" className={inp} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="flex flex-col">Hasta
              <input type="date" className={inp} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <label className="flex flex-col">Cliente
              <input className={`${inp} w-44`} placeholder="Nombre…" value={fCliente} onChange={(e) => setFCliente(e.target.value)} /></label>
            <label className="flex flex-col">RUC / CI
              <input className={`${inp} w-36`} inputMode="numeric" value={fRuc} onChange={(e) => setFRuc(e.target.value)} /></label>
            <label className="flex flex-col">N° documento
              <input className={`${inp} w-40 font-mono`} placeholder="001-101-…" value={fNumero} onChange={(e) => setFNumero(e.target.value)} /></label>
            <label className="flex flex-col">Estado
              <select className={inp} value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
                <option value="">Todos</option>
                {estados.map((k) => <option key={k} value={k}>{ESTADO_EMISION[k]?.label ?? k}</option>)}
              </select></label>
            {hayFiltro && <Button size="sm" variant="outline" onClick={() => { setDesde(''); setHasta(''); setFCliente(''); setFRuc(''); setFNumero(''); setFEstado(''); }}>Limpiar</Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th>
              <th className="py-2 pr-3">N° Documento</th>
              <th className="py-2 pr-3">Cliente</th>
              <th className="py-2 pr-3 text-right">Líneas</th>
              <th className="py-2 pr-3 text-right">Importe</th>
              <th className="py-2 pr-3">Estado</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtradas.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{hayFiltro ? 'Ningún comprobante coincide con los filtros' : 'Sin comprobantes generados'}</td></tr>}
            {filtradas.map((d) => (
              <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-2 pr-3 whitespace-nowrap">{d.fecha_emision}</td>
                <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{d.serie_comprobante || <span className="italic text-muted-foreground">Sin emitir</span>}</td>
                <td className="py-2 pr-3">
                  <span className="truncate max-w-64 inline-block align-middle">{d.cliente || '—'}</span>
                  {d.ruc_receptor && <span className="block font-mono text-[11px] text-muted-foreground">{d.ruc_receptor}</span>}
                </td>
                <td className="py-2 pr-3 text-right">{d.lineas}</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(d.importe_total)}</td>
                <td className="py-2 pr-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${(ESTADO_EMISION[d.estado_emision] || ESTADO_EMISION.por_emitir).cls}`}
                    title={d.mensaje_sri || ''}>
                    {(ESTADO_EMISION[d.estado_emision] || ESTADO_EMISION.por_emitir).label}
                  </span>
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"
                    title={d.estado_emision === 'autorizado' ? 'Ver la factura en PDF' : 'Ver PDF en borrador (proforma — sin validez tributaria hasta emitirse)'}
                    onClick={() => { void abrirFacturaPdf({ ...d, serie_comprobante: null }).catch((e) => toast.error(e?.message || 'No se pudo abrir el PDF')); }}>
                    <FileText className="w-4 h-4" /> PDF
                  </button>
                  {urlDoc(d.ride_url) && (
                    <a href={urlDoc(d.ride_url)!} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle" title="Descargar RIDE (PDF)">
                      <FileText className="w-4 h-4" /> RIDE
                    </a>
                  )}
                  {urlDoc(d.xml_url) && (
                    <a href={urlDoc(d.xml_url)!} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle" title="Descargar XML autorizado">
                      <Download className="w-4 h-4" /> XML
                    </a>
                  )}
                  {EMITIBLE.includes(d.estado_emision) && (
                    <button className="inline-flex items-center justify-center w-7 h-7 mr-1 rounded-md hover:bg-muted align-middle" title="Editar" onClick={() => onEdit(d.id)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {EMITIBLE.includes(d.estado_emision) && (
                    <Button size="sm" isLoading={emitir.isPending} onClick={() => emitir.mutate(d.id)}>
                      Emitir
                    </Button>
                  )}
                  {d.estado_emision === 'encolado' && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" /> procesando…
                    </span>
                  )}
                  {EMITIBLE.includes(d.estado_emision) && (
                    <button className="inline-flex items-center justify-center w-7 h-7 ml-1 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 align-middle"
                      title="Eliminar"
                      onClick={() => setDescartar({ id: d.id, serie_comprobante: d.serie_comprobante, estado_emision: d.estado_emision })}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {d.estado_emision === 'autorizado' && isAdmin() && (
                    <button className="inline-flex items-center justify-center w-7 h-7 ml-1 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 align-middle"
                      title="Anular (ya anulada en el portal del SRI o con nota de crédito)"
                      onClick={() => setAnular({ id: d.id, serie_comprobante: d.serie_comprobante, cliente: d.cliente, importe_total: d.importe_total })}>
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mt-3">
          Los comprobantes quedan "Por emitir". Al pulsar <strong>Emitir</strong> se firman y envían al SRI; una vez
          <strong> Autorizado</strong> quedan disponibles el XML autorizado y el RIDE (PDF) para descargar.
        </p>
        {descartar && <DescartarDialog doc={descartar} onClose={() => setDescartar(null)}
          onDone={() => void queryClient.invalidateQueries()} />}
        {anular && <AnularDialog doc={anular} onClose={() => setAnular(null)}
          onDone={() => void queryClient.invalidateQueries()} />}
      </CardContent>
    </Card>
  );
}

// Agrupa cabecera de comprobante + sus ítems sin nodo DOM extra dentro del <tbody>
function FragmentoGrupo({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
